import { useEffect } from 'react';
import Layout from '../../../components/layout/Layout';
import Breadcrumb from '../../../components/layout/Breadcrumb';
import { updateSEO, seoData } from '../../../utils/seo';

// ===== 執事の守秘指針（route=/standards/confidentiality）=====
// デザイン土台: 紺#1a2942 / 真鍮金#a38e65 / BTN-08 / 紺帯番号バンド / 白リッチカード / 細罫エディトリアル行
// /standards/hospitality と同型セクションの使い回しはしない（構成を変えた新バリエーション）
// 事実正典: 創立1890年（明治23年）/ 法人設立2025年2月19日 / 代表理事 石井裕一（三代目頭取）/
//   資格は初級Associate・中級Professional・上級Masterの3段階+専門資格
//   協会と執事とお客様の間で双方向の守秘義務契約を結ぶ
// 創作した年号・数値・実績・違反件数・罰則は書かない

export default function StandardsConfidentialityPage() {
  useEffect(() => {
    updateSEO(seoData['/standards/confidentiality']);
  }, []);

  const breadcrumbs = [
    { label: 'ホーム', href: '/' },
    { label: '基準・指針', href: '/standards' },
    { label: '執事の守秘指針' }
  ];

  const BTN08_ON_LIGHT =
    "relative inline-flex items-center justify-center bg-[#1a2942] text-[#f8f6f0] border border-[#a38e65] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#a38e65] hover:text-[#1a2942] hover:border-[#1a2942] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#a38e65]/45 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#1a2942]/45";
  const BTN08_ON_NAVY =
    "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const secretAxes = [
    {
      en: 'Household',
      ja: '家族構成',
      text: '同居・別居の別、続柄、呼称、日常の役割分担など、家の内側の地図そのものが秘密です。来客の前で家族の事情を補足説明しないことが基本です。'
    },
    {
      en: 'Visitors',
      ja: '来客',
      text: '誰がいつ訪れたか、何を話したか、どのような手配をしたか。訪問の事実そのものが機微であり、他の来客や外部へ漏らさない姿勢を保ちます。'
    },
    {
      en: 'Health',
      ja: '健康',
      text: '体調、通院、食事制限、休息の取り方など、身体に関わる情報は最も慎重に扱います。必要最小限の共有にとどめ、憶測での伝達は行いません。'
    },
    {
      en: 'Assets',
      ja: '資産',
      text: '住まいの設備、所蔵品、契約先、金銭の動きに触れる事柄は、業務上必要な範囲以外では口にしません。見聞きした事実を話題の種にしないことが品位です。'
    },
    {
      en: 'Keys',
      ja: '鍵とアクセス',
      text: '鍵、暗証、入退の手順、保管場所、臨時の解錠権限は、家の安全そのものです。所持と返却の記録を厳格にし、私的な複製や口外を禁じます。'
    }
  ];

  const barrierLayers = [
    {
      title: '家族のあいだ',
      body: '依頼者本人から預かった話を、配偶者やご家族へ自動的に共有してはなりません。共有の可否は本人の指示に従い、曖昧なときは確認を挟みます。善意の「伝えておきました」が信頼を損なうことがあります。'
    },
    {
      title: 'スタッフのあいだ',
      body: '厨房・運転・清掃など役割が分かれる現場では、業務に必要な要点だけを渡します。興味や噂で情報を広げない。引き継ぎメモにも評価や憶測を混ぜず、事実と指示に限定します。'
    },
    {
      title: '外部との境界',
      body: '取引先、来訪業者、知人からの何気ない質問にも、家の内側を推測させる答え方を避けます。沈黙と簡潔な取次が、最も誠実な応対になる場面があります。'
    }
  ];

  const recordRules = [
    {
      no: '01',
      title: '撮影の前提',
      text: '業務上の記録写真であっても、依頼者の許可なき撮影は行いません。許可がある場合も、写り込む人物・住所・名札・書類が最小になる構図を選びます。'
    },
    {
      no: '02',
      title: '保存の範囲',
      text: '記録は奉仕の連続性のためだけに残します。私的な端末への持ち出し、クラウドへの無秩序な同期、個人のアルバム化は避け、保管場所と閲覧権限を定めます。'
    },
    {
      no: '03',
      title: '破棄と返却',
      text: '用済みのメモ、複製、下書きは速やかに破棄または返却します。契約終了時には、手元に残る媒体がないかを点検し、指示された方法で引き渡します。'
    },
    {
      no: '04',
      title: '文言の品位',
      text: '残す文章から、感情的な評価や第三者を傷つける表現を除きます。後に読む人が業務を再現できる簡潔さだけを残すことが、記録の倫理です。'
    }
  ];

  const inquirySteps = [
    {
      en: 'Pause',
      ja: 'いったん受ける',
      text: '電話・訪問・書面いずれでも、即答で事実を認めたり否定したりしません。相手の所属と用件を控え、回答権限の有無を内側で確認する時間を取ります。'
    },
    {
      en: 'Verify',
      ja: '経路を確かめる',
      text: '依頼者本人、またはあらかじめ指定された連絡窓口を通じて、照会への対応方針を確認します。急かす言葉に押され、正規の経路を省略しません。'
    },
    {
      en: 'Limit',
      ja: '返す範囲を絞る',
      text: '回答が許される場合も、必要最小限の文言に留めます。推測や私見、関連しそうな周辺事情を添えて「親切」を演出しないことが守秘です。'
    },
    {
      en: 'Log',
      ja: '経緯を残す',
      text: '誰から、いつ、何を問われ、どう応じたかを簡潔に記録します。記録自体も秘密として扱い、共有先を限定します。'
    }
  ];

  const reportFlow = [
    '異変に気づいた本人が、憶測を混ぜず「いつ・何を・どこで」見聞きしたかを整理する',
    '独断で関係者へ広く連絡せず、あらかじめ定められた報告先（現場責任者または協会の指定窓口）へ上げる',
    '証拠となる媒体やメモがある場合は、改変せず保全し、指示を待って提出する',
    '依頼者への説明の要否・文言・順序は、個人の判断で進めず方針の確認後に行う',
    '再発を防ぐため、手順や保管の弱点があれば、感情論ではなく運用の改善点として共有する'
  ];

  const faqItems = [
    {
      q: '家族からの質問であれば、依頼者の話を伝えてもよいですか？',
      a: 'ご家族であっても、自動的に共有してよいわけではありません。本人から共有の指示がある範囲に限ります。判断に迷うときは、伝えてしまう前に本人または定められた窓口へ確認してください。善意の先回りが、家の信頼関係を損なうことがあります。'
    },
    {
      q: '契約が終わったあと、昔のエピソードを研修で話してもよいですか？',
      a: '契約終了後も守秘義務は続きます。個人や家が特定できるエピソード、推測できる细节は、研修・会話・執筆のいずれでも用いません。一般化した教訓として語る場合も、特定性を取り除けているかを厳しく点検してください。'
    },
    {
      q: '第三者から「知り合いのはず」と身元を示された場合は？',
      a: '身元の自己申告だけでは開示の根拠になりません。依頼者が認めた経路かどうかを確認し、確認が取れるまで事実関係に触れない応対を徹底します。親しみのある口調や急かす期限提示にも、手順を省略しないことが職能です。'
    }
  ];

  const articleLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: '執事の守秘指針｜全日本執事協会が定める基準',
    description:
      '全日本執事協会が定める執事の守秘指針。秘密の範囲、契約終了後の義務、家族間・スタッフ間の情報遮断、写真と記録、SNS、第三者照会、漏えいが疑われるときの報告手順。',
    author: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    publisher: {
      '@type': 'Organization',
      name: '一般社団法人 全日本執事協会'
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': 'https://japanbutlerassociation.com/standards/confidentiality'
    }
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'ホーム', item: 'https://japanbutlerassociation.com/' },
      { '@type': 'ListItem', position: 2, name: '基準・指針', item: 'https://japanbutlerassociation.com/standards' },
      { '@type': 'ListItem', position: 3, name: '執事の守秘指針', item: 'https://japanbutlerassociation.com/standards/confidentiality' }
    ]
  };

  const faqLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.a
      }
    }))
  };

  return (
    <Layout>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqLd) }} />

      {/* ヒーロー — 全幅ビルボード */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/generated/jba-std-confidentiality-01.jpg"
          alt="静かな室内で姿勢を正し、守秘の覚悟を示す執事の佇まい"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[720px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">
              Confidentiality Standards
            </span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">執事の守秘指針</span>
              <span className="block text-[#d7b779]">沈黙が守る信頼の基準</span>
            </h1>
            <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
              一般社団法人 全日本執事協会は、1890年（明治23年）の創立以来、日本における執事文化の継承に取り組んできました。代表理事・石井裕一（三代目頭取）のもと、本指針は認定団体である当協会が定める守秘の基準です。協会・執事・お客様のあいだで双方向の守秘義務契約を結ぶことを前提に、何を秘密とし、どこまで沈黙し、漏えいが疑われるときにどう動くかを示します。接遇の華やかさの奥で、静かに家を守るための座標です。
            </p>
          </div>
        </div>
      </section>

      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <div className="bg-white">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20 space-y-20 sm:space-y-24">

          {/* S1. 基準化の理由 — 引用帯＋二段本文＋横並び原則（hospitalityの三指標グリッドとは別型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              I. Why We Codify
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              守秘を<span className="text-[#a38e65]">基準化する理由</span>
            </h2>
            <div className="border-l-2 border-[#a38e65] bg-[#fbfaf7] px-6 sm:px-8 py-6 mb-8">
              <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm tracking-[0.06em] mb-3">
                Silence is part of the craft
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#1a2942] leading-[1.95]">
                守秘は個人の性格や「口が堅い」という評判に委ねるものではありません。誰が、何を、どの範囲で沈黙するかを共有言語にしなければ、善意の会話が家の境界を侵します。
              </p>
            </div>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                執事は、依頼者の日常の奥へ立ち入る職能です。家族の機微、来客の顔ぶれ、健康、資産、鍵——見聞きするほど奉仕の精度は上がりますが、同時に預かりものの重さも増します。属人的な裁量だけに任せるのではなく、協会として守るべき線を明示すること。それが本指針の役割です。
              </p>
              <p>
                当協会は、Associate・Professional・Masterの三段階と専門資格を通じて技能を育てると同時に、協会・執事・お客様のあいだで双方向の守秘義務契約を結ぶことを求めています。一方的な誓約ではなく、互いに沈黙の責任を引き受ける関係です。倫理の骨格は
                <a href="/standards/ethics" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">倫理規定</a>
                と、情報面の実務は
                <a href="/standards/infosec" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">情報セキュリティ</a>
                と併せて参照し、基準全体の見取り図は
                <a href="/standards" className="text-[#1a2942] underline underline-offset-4 decoration-[#a38e65] mx-1">基準・指針</a>
                から俯瞰できます。
              </p>
            </div>
            <div className="mt-10 grid sm:grid-cols-3 gap-6">
              {[
                { en: 'Clarity', ja: '線を見える化する', sub: '秘密の範囲と共有の可否を、現場の共通語にする' },
                { en: 'Continuity', ja: '時間を超えて守る', sub: '在任中も契約後も、沈黙の責任を途切れさせない' },
                { en: 'Reciprocity', ja: '双方向で引き受ける', sub: '協会・執事・お客様が互いに守秘の義務を負う' }
              ].map((c) => (
                <div key={c.en} className="border-t-2 border-[#1a2942] pt-5">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{c.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-2">{c.ja}</span>
                  <span className="block font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">{c.sub}</span>
                </div>
              ))}
            </div>
          </section>

          {/* S2. 秘密の範囲 — 五軸カード＋下画像（hospitalityの三軸とは別） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              II. What Counts as Secret
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              何が秘密にあたるか
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              秘密とは、鍵のかかった書類だけを指すのではありません。日常の会話、スケジュール、好みの裏側にある事情、玄関先で交わした短い言葉まで、家の外へ出せば誰かの安心を揺るがす事柄はすべて対象です。以下の五軸は、現場で迷いやすい代表例です。列挙にない事柄でも、「外に出して家が傷つくか」を自問する習慣を優先してください。
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[#e2ddd2] border border-[#e2ddd2] mb-8">
              {secretAxes.map((p) => (
                <div key={p.en} className="bg-[#fbfaf7] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[12px] tracking-[0.08em] mb-2">{p.en}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{p.ja}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{p.text}</p>
                </div>
              ))}
            </div>
            <div className="grid lg:grid-cols-[1fr_320px] gap-8 items-center">
              <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
                <p>
                  秘密の判定で大切なのは、情報の「珍しさ」ではなく、開示されたときの影響です。ありふれた生活の断片でも、組み合わせれば家や個人が特定されることがあります。断片を雑談の彩りに使わない。それが職能としての最低線です。
                </p>
                <p>
                  また、依頼者が自ら公の場で触れた話題であっても、執事側から詳細を補ったり、裏事情を付け加えたりする権限は生まれません。公開の度合いは常に本人が決めるものであり、奉仕者が先回りして広げてはなりません。
                </p>
              </div>
              <img
                src="/images/generated/jba-std-confidentiality-02.jpg"
                alt="守秘の共有範囲を確認するため静かに打ち合わせる執事たち"
                className="w-full h-56 lg:h-[260px] object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S3. 契約終了後 — 縦タイムライン型（hospitalityにない型） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              III. After the Engagement
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              契約終了後も続く義務
            </h2>
            <div className="grid lg:grid-cols-[300px_1fr] gap-10 items-start">
              <img
                src="/images/generated/jba-std-confidentiality-03.jpg"
                alt="守秘の基本姿勢を学ぶ研修の一場面"
                className="w-full h-64 object-cover border border-[#a38e65]/30"
              />
              <div>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8">
                  守秘は在任中のルールではありません。契約が終わり、鍵を返却し、現場を離れたあとも、預かった事実は執事の内側に留まります。退職・移籍・引退のいずれであっても、過去の家を話題の資源にしてはなりません。双方向の守秘義務契約は、時間の経過で自動的に薄れる性質のものではない、という理解が前提です。
                </p>
                <div className="space-y-0 border-t border-[#1a2942]">
                  {[
                    { t: '在任中', d: '業務に必要な共有以外を閉じ、記録の所在と閲覧者を明確にする。私的な会話への持ち出しを戒める。' },
                    { t: '終了手続', d: '鍵・媒体・メモ・複製の返却と破棄を点検する。残置の有無を自己申告に頼らず、手順として確認する。' },
                    { t: '終了後', d: '特定可能なエピソードを語らない。推薦・回想・執筆・研修のいずれでも、家と個人が浮かぶ细节を除く。' },
                    { t: '再縁がある場合', d: '再び関与する機会があっても、過去の機微を「知っている前提」で持ち出さない。その都度、必要な範囲だけを改めて預かる。' }
                  ].map((row, idx) => (
                    <div key={row.t} className="flex gap-5 py-5 border-b border-[#e2ddd2]">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">{String(idx + 1).padStart(2, '0')}</span>
                      <div>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{row.t}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{row.d}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* S4. 情報の遮断 — 紺帯＋三層（構成は近いが中身と導入が別） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IV. Internal Partitions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
              家族間・スタッフ間での情報の遮断
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              守秘の失敗は、外部への漏洩だけではありません。家の内側で、伝える必要のない相手へ話が渡ることでも信頼は傷つきます。家族だから、同僚だから、という近さは、開示の許可を意味しません。遮断とは冷淡さではなく、それぞれの関係を守るための仕切りです。
            </p>
            <div className="bg-[#1a2942] px-6 sm:px-8 py-4 mb-8 flex flex-wrap items-center gap-x-8 gap-y-2">
              <span className="font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.18em] text-sm">PARTITION</span>
              <span className="font-['Noto_Serif_JP',serif] text-[#f8f6f0]/85 text-sm tracking-[0.06em]">
                近さは、開示の許可ではない
              </span>
            </div>
            <div className="grid lg:grid-cols-3 gap-6 mb-8">
              {barrierLayers.map((layer, idx) => (
                <div key={layer.title} className="bg-white border border-[#e2ddd2] shadow-[0_12px_40px_rgba(26,41,66,0.06)] p-7 flex flex-col">
                  <span className="font-['Playfair_Display',serif] text-[#d7b779] text-2xl mb-3">{String(idx + 1).padStart(2, '0')}</span>
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3">{layer.title}</h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">{layer.body}</p>
                </div>
              ))}
            </div>
            <div className="bg-[#fbfaf7] border border-[#e2ddd2] p-7 sm:p-8">
              <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                引き継ぎが必要なときは、感想や人物評を混ぜず、事実・期限・確認済みの希望だけを渡します。「あの方はこういう人だ」という要約は、しばしば守秘と公平を同時に壊します。遮断の技術は、沈黙だけでなく、渡す言葉を削る技術でもあります。
              </p>
            </div>
          </section>

          {/* S5. 写真と記録 + SNS — 記録媒体と公開発信を統合（section1つ・旧SNSのh2をh3へ） */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              V. Images & Notes
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              写真と記録の扱い
            </h2>
            <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 items-start">
              <div className="order-2 lg:order-1 border-r-0 lg:border-r-2 border-[#a38e65] pr-0 lg:pr-8">
                <p className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-sm mb-3">Least retained</p>
                <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] mb-6">
                  残さない勇気が、最も美しい記録管理です。便利さのために撮り溜め、転送し、個人の端末へ残す習慣は、守秘の穴を静かに広げます。必要が消えた瞬間に消す——その spoed を現場の作法にしてください。
                </p>
                <img
                  src="/images/generated/jba-std-confidentiality-04.jpg"
                  alt="記録の点検と取扱いを確認する手元の作業"
                  className="w-full h-52 object-cover border border-[#a38e65]/30"
                />
              </div>
              <div className="order-1 lg:order-2">
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-6">
                  写真とメモは、奉仕の再現性を支える道具であると同時に、一度外へ出れば回収の難しい証拠にもなります。撮る・残す・共有する・捨てる、の各段階に許可と目的が必要です。情報面の技術的な守り方は、情報セキュリティの指針と読み合わせ、本指針では「何を残さないか」という倫理側を主に示します。
                </p>
                <div className="border-t border-[#1a2942]">
                  {recordRules.map((item) => (
                    <div key={item.no} className="flex gap-5 py-5 border-b border-[#e2ddd2]">
                      <span className="font-['Playfair_Display',serif] text-[#d7b779] text-xl shrink-0 w-8">{item.no}</span>
                      <div>
                        <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-2">{item.title}</h3>
                        <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.9]">{item.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-16 pt-12 border-t border-[#e2ddd2]">
              <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
                VI. No Public Echo
              </span>
              <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
                SNSでの言及禁止
              </h3>
              <div className="grid lg:grid-cols-2 gap-0 border border-[#1a2942] mb-8">
                <div className="bg-[#1a2942] p-8 sm:p-10">
                  <span className="block font-['Playfair_Display',serif] text-[#d7b779] tracking-[0.16em] text-sm mb-4">PROHIBITED</span>
                  <ul className="space-y-4">
                    {[
                      '勤務先や依頼者が特定できる地名・建物・車・制服の写り込み',
                      '「今日の仕事」を匂わせる投稿、ストーリーズ、私的な配信',
                      '匿名のつもりでも組み合わせで辿れるエピソードの断片',
                      '同僚や来客を巻き込んだタグ付け、位置情報、共同投稿'
                    ].map((line) => (
                      <li key={line} className="flex gap-3 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                        <span className="text-[#a38e65] shrink-0">—</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-[#fbfaf7] p-8 sm:p-10">
                  <span className="block font-['Playfair_Display',serif] text-[#8f7a56] tracking-[0.16em] text-sm mb-4">DISCIPLINE</span>
                  <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95]">
                    <p>
                      SNSは私的な発信の場に見えて、実際には検索と保存が前提の公開空間です。「友人だけが見る」「すぐに消す」は、守秘の根拠になりません。職能に就いた時点で、家に関する言及は閉じる——その簡潔な禁止が、最も誤解の少ない基準です。
                    </p>
                    <p>
                      称賛の言葉や感謝の投稿であっても、相手の同意と文脈がなければ控えます。沈黙は欠礼ではなく、相手の生活を守るための礼です。私的なアカウントと業務用を分けていても、判断の軸は同じです。
                    </p>
                  </div>
                </div>
              </div>
              <img
                src="/images/gen2/std1-confidentiality-secure-folder.jpg"
                alt="封筒を保管棚へ収めて情報を守る執事"
                className="w-full h-56 sm:h-72 object-cover border border-[#a38e65]/30"
              />
            </div>
          </section>

          {/* S7. 第三者照会 — 横四ステップ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VII. Third-Party Inquiries
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-5">
              第三者からの照会への応対
            </h2>
            <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] mb-8 max-w-3xl">
              電話、訪問、書面、紹介経由の口頭——形は違っても、家の外から事実を確かめてこようとする力は常に存在します。親しみや権威、緊急性を装った照会ほど、手順を飛ばさせようとします。応対の品位は、速さではなく、正規の経路を守る静けさに現れます。
            </p>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 border-t-2 border-[#1a2942] mb-8">
              {inquirySteps.map((s) => (
                <li key={s.en} className="border-b sm:border-b lg:border-b-0 lg:border-r last:border-r-0 border-[#e2ddd2] px-5 py-6">
                  <span className="block font-['Playfair_Display',serif] text-[#a38e65] text-sm tracking-[0.12em] mb-2">{s.en}</span>
                  <span className="block jba-display font-medium text-[#1a2942] text-lg mb-3">{s.ja}</span>
                  <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                </li>
              ))}
            </ol>
            <div className="space-y-[18px] font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              <p>
                「知っているはず」「家族だ」「以前も話した」といった前置きは、開示を急がせるための言葉であることが少なくありません。相手の物語に乗らず、こちらの手順に戻す。それが第三者応対の基本動作です。回答できない旨を伝えるときも、理由を長々と説明して逆に事情を漏らさないよう、短く、丁寧に、閉じます。
              </p>
            </div>
          </section>

          {/* S8. 漏えい報告 — 紺パネル＋番号リスト＋画像 */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              VIII. When Leakage is Suspected
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              漏えいが疑われるときの報告手順
            </h2>
            <div className="bg-[#1a2942] p-8 sm:p-10 lg:p-12">
              <div className="grid lg:grid-cols-[1fr_300px] gap-10 items-start">
                <div>
                  <p className="font-['Noto_Serif_JP',serif] text-base text-[#e7e2d8] leading-[1.95] mb-8">
                    疑わしい兆候に気づいたとき、個人の判断で隠すことも、逆に周囲へ広く言いふらすことも適切ではありません。事実を整え、定められた経路へ上げ、保全と説明の順序を守る。感情より手順を先に置くことが、家と職能の双方を守ります。創作の罰則や件数ではなく、現場が迷わず動ける順序を以下に示します。
                  </p>
                  <ul className="space-y-4">
                    {reportFlow.map((line, idx) => (
                      <li key={line} className="flex gap-4 font-['Noto_Serif_JP',serif] text-[15px] text-[#f8f6f0] leading-[1.85]">
                        <span className="font-['Playfair_Display',serif] text-[#a38e65] shrink-0">{String(idx + 1).padStart(2, '0')}</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <img
                  src="/images/generated/jba-std-confidentiality-06.jpg"
                  alt="落ち着いた判断で報告の順序を統括する上級執事の姿勢"
                  className="w-full h-56 lg:h-[340px] object-cover border border-[#a38e65]/40"
                />
              </div>
            </div>
            <p className="mt-8 font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95]">
              報告は、誰かを糾弾するための儀式ではありません。被害の拡大を止め、再発の穴を塞ぐための実務です。Master Butlerが示す統括の視点も、現場の信号を個人の秘密にせず、運用へ戻すところにあります。資格の段階にかかわらず、気づいた者が上げる——その単純な勇気を、本指針は求めます。
            </p>
          </section>

          {/* S9. FAQ */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              IX. Questions
            </span>
            <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              よくある問い
            </h2>
            <div className="border-t-2 border-[#1a2942]">
              {faqItems.map((item) => (
                <div key={item.q} className="border-b border-[#e2ddd2] py-6">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg mb-3 flex gap-3">
                    <span className="text-[#a38e65] font-['Playfair_Display',serif] shrink-0">Q</span>
                    <span>{item.q}</span>
                  </h3>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#333] leading-[1.95] pl-8">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* S10. 関連導線 — 隣接ナビhref重複なし */}
          <section>
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">
              X. Related Paths
            </span>
            <h3 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
              関連する基準と学びへ
            </h3>
            <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                守秘指針は、倫理の骨格と情報の守り方とあわせて読むことで実践に落ちます。資格制度を通じて判断力を段階的に育て、現場での沈黙に再現性を与えること。全日本執事協会は、1890年（明治23年）から続く系譜のうえで、いまの暮らしにふさわしい奉仕の座標を示し続けます。
              </p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-6">
                <a href="/standards/ethics" className={BTN08_ON_NAVY}>
                  倫理規定を読む
                </a>
                <a href="/standards/infosec" className={BTN08_ON_NAVY}>
                  情報セキュリティ
                </a>
                <a href="/qualifications" className={BTN08_ON_NAVY}>
                  資格制度を見る
                </a>
              </div>
            </div>
          </section>

        </div>
      </div>
    </Layout>
  );
}
